import { ChevronRight, Database, LoaderCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import useSemanticObjects from '@/frontend/features/Fde/useSemanticObjects'
import { cn } from '@/lib/utils'
import type { Id, SemanticObjectColumn, SemanticObjectInstance } from '@/frontend/features/Fde/types'

interface Props {
  projectId: Id
  classId: Id
  copy: { workspace: Record<string, string>; common: Record<string, string> }
  dataAssetId?: Id | null
  limit?: number
  compact?: boolean
  onSelect?: (object: SemanticObjectInstance) => void
}

export default function SemanticObjectPreview(props: Props) {
  const { result, error, loading } = useSemanticObjects({
    projectId: props.projectId,
    classId: props.classId,
    dataAssetId: props.dataAssetId,
    limit: props.limit,
    enabled: true,
    errorLabel: props.copy.workspace.semantic_objects_error,
  })

  if (loading) return <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-4 text-xs text-muted-foreground"><LoaderCircle className="size-3.5 animate-spin" />{props.copy.workspace.semantic_objects_loading}</div>
  if (error) return <div className="rounded-lg border border-dashed border-border px-3 py-4 text-xs leading-5 text-muted-foreground">{error}</div>
  if (!result) return null

  const columns = visibleColumns(result.columns, props.compact)
  const sourceCount = result.page.source_count

  return <section className="overflow-hidden rounded-xl border border-border bg-background">
    <header className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-3 py-2.5">
      <Database className="size-3.5 text-primary" />
      <strong className="text-xs text-foreground">{props.copy.workspace.semantic_objects_title}</strong>
      <span className="max-w-80 truncate text-[10px] text-muted-foreground">{(result.sources || [result.source]).map((source) => source.name).join(', ')}</span>
      <Badge variant={result.source.binding_status === 'confirmado' ? 'success' : 'warning'} className="ml-auto text-[9px]">
        {sourceCount == null ? result.page.returned : sourceCount} {props.copy.workspace.semantic_objects_count}
      </Badge>
    </header>
    {result.objects.length === 0 ? <p className="px-4 py-8 text-center text-xs text-muted-foreground">{props.copy.workspace.semantic_objects_empty}</p> : <div className="overflow-x-auto">
      <table className="w-full min-w-[38rem] text-left text-xs">
        <thead className="border-b border-border bg-muted/10 text-[9px] uppercase tracking-wider text-muted-foreground">
          <tr><th className="px-3 py-2 font-semibold">{props.copy.workspace.semantic_objects_object}</th>{columns.map((column) => <th key={column.key} className="px-3 py-2 font-semibold">{column.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {result.objects.map((object) => <tr key={object.id} className="align-top hover:bg-muted/20">
            <td className="max-w-56 px-3 py-2.5 font-medium text-foreground">{props.onSelect ? <button type="button" className="flex w-full items-center gap-2 text-left hover:text-primary" onClick={() => props.onSelect?.(object)}><span className="min-w-0 flex-1 truncate">{object.title}</span>{!props.compact && <span className="shrink-0 text-[9px] font-medium text-primary">{props.copy.workspace.semantic_object_view_open}</span>}<ChevronRight className="size-3.5 shrink-0" /></button> : object.title}</td>
            {columns.map((column) => <td key={column.key} className="max-w-52 px-3 py-2.5 text-muted-foreground">{formatValue(object.properties[column.key], props.copy)}</td>)}
          </tr>)}
        </tbody>
      </table>
    </div>}
    <footer className={cn('border-t border-border px-3 py-2 text-[9px] leading-4 text-muted-foreground', props.compact && 'sr-only')}>
      {props.copy.workspace.semantic_objects_sample.replace('%{shown}', String(result.page.returned)).replace('%{total}', String(sourceCount ?? result.page.returned))}
    </footer>
  </section>
}

function visibleColumns(columns: SemanticObjectColumn[], compact = false) {
  const withoutTitle = columns.filter((column) => column.identity_role !== 'title')
  return withoutTitle.slice(0, compact ? 3 : 6)
}

function formatValue(value: unknown, copy: Props['copy']) {
  if (value == null || value === '') return '—'
  if (typeof value === 'boolean') return value ? copy.common.yes : copy.common.no
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
